import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { ThreeCanvas } from "@remotion/three";
import { ZuiMesh, BoloMesh } from "./ZuiAndBolo3DReborn";

export const ZuiAndBoloArenaShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Gentle studio camera breathing
  const camBob = Math.sin(frame * 0.05) * 0.05;

  return (
    <div
      style={{
        width,
        height,
        backgroundColor: "#09090B",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      {/* --- BACKGROUND SUBTLE GRID & GLOW --- */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 30% 40%, rgba(0, 229, 255, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 70% 40%, rgba(192, 132, 252, 0.09) 0%, transparent 50%),
            linear-gradient(to bottom, rgba(15, 23, 42, 0.6), rgba(9, 9, 11, 0.95))
          `,
        }}
      />

      {/* --- 3D CANVAS STAGE --- */}
      <div style={{ position: "absolute", inset: 0, zIndex: 10 }}>
        <ThreeCanvas
          width={width}
          height={height}
          camera={{
            fov: 38,
            position: [0, 0.2 + camBob, 4.8],
          }}
          shadows
        >
          {/* Ambient Lighting */}
          <ambientLight intensity={0.9} color="#FFFFFF" />

          {/* Key Light (Top-Front Warm) */}
          <directionalLight
            castShadow
            position={[-3, 8, 5]}
            intensity={3.2}
            color="#FFF8EE"
            shadow-mapSize={[2048, 2048]}
            shadow-bias={-0.0003}
          />

          {/* Fill Light (Soft Cool Front) */}
          <directionalLight position={[4, 3, 4]} intensity={1.4} color="#E0E7FF" />

          {/* Rim Light 1: Laser Cyan (Left / Zui) */}
          <directionalLight position={[-5, 6, -5]} intensity={4.2} color="#00E5FF" />

          {/* Rim Light 2: Radiant Violet (Right / Bolo) */}
          <directionalLight position={[5, 6, -5]} intensity={4.2} color="#C084FC" />

          {/* Floor Bounce */}
          <pointLight position={[0, -0.8, 2]} intensity={1.5} color="#FED7AA" distance={6} />

          {/* --- MINIMALIST OBSIDIAN PODIUM --- */}
          <group position={[0, -1.15, 0]}>
            <mesh receiveShadow position={[0, 0, 0]}>
              <cylinderGeometry args={[3.2, 3.2, 0.12, 48]} />
              <meshStandardMaterial color="#141418" roughness={0.4} metalness={0.2} />
            </mesh>
            <mesh position={[0, -0.12, 0]}>
              <cylinderGeometry args={[3.28, 3.45, 0.18, 48]} />
              <meshStandardMaterial color="#0A0A0C" roughness={0.6} metalness={0.5} />
            </mesh>
            {/* Cyan indicator ring under Zui */}
            <mesh position={[-0.95, 0.065, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0.9, 0.96, 36]} />
              <meshBasicMaterial color="#00E5FF" />
            </mesh>
            {/* Violet indicator ring under Bolo */}
            <mesh position={[0.95, 0.065, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0.9, 0.96, 36]} />
              <meshBasicMaterial color="#C084FC" />
            </mesh>
          </group>

          {/* --- REBORN ZUI 3D (CHAMPION a001) --- */}
          <group position={[-0.95, -0.55, 0]} rotation={[0, 0.25, 0]}>
            <ZuiMesh frame={frame} fps={fps} isSpeaking={frame % 120 < 60} />
          </group>

          {/* --- REBORN BOLO 3D (CHAMPION COMPANION) --- */}
          <group position={[0.95, -0.55, 0]} rotation={[0, -0.25, 0]}>
            <BoloMesh
              frame={frame}
              fps={fps}
              isSpeaking={frame % 120 >= 60}
              isShocked={frame % 150 > 120}
            />
          </group>
        </ThreeCanvas>
      </div>

      {/* --- HUD HEADER & OVERLAY (SAFE ZONE TIKTOK Y 220px - 1520px) --- */}
      <div
        style={{
          position: "absolute",
          top: 240,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 50,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "rgba(0, 229, 255, 0.12)",
            border: "1px solid rgba(0, 229, 255, 0.35)",
            padding: "6px 16px",
            borderRadius: 999,
            color: "#00E5FF",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            marginBottom: 12,
            boxShadow: "0 0 20px rgba(0, 229, 255, 0.25)",
          }}
        >
          <span>⚔️</span> ARENA SURVIVOR • 16 AGENTS CLASH
        </div>
        <h1
          style={{
            color: "#FFFFFF",
            fontSize: 48,
            fontWeight: 800,
            letterSpacing: -1,
            margin: 0,
            textAlign: "center",
            textShadow: "0 4px 20px rgba(0, 0, 0, 0.8)",
          }}
        >
          ZUI & BOLO 3D REBORN
        </h1>
        <p
          style={{
            color: "#A1A1AA",
            fontSize: 18,
            margin: "8px 0 0 0",
            textAlign: "center",
          }}
        >
          Procedural Three.js Meshes • Pure Remotion Frame-Deterministic
        </p>
      </div>

      {/* --- FOOTER CARD (TIKTOK SAFE ZONE Y: ~1400px) --- */}
      <div
        style={{
          position: "absolute",
          bottom: 380,
          left: 80,
          right: 80,
          backgroundColor: "rgba(18, 18, 24, 0.82)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          backdropFilter: "blur(16px)",
          borderRadius: 24,
          padding: "20px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
          boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              backgroundColor: "rgba(0, 229, 255, 0.15)",
              border: "1px solid rgba(0, 229, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
            }}
          >
            🐾
          </div>
          <div>
            <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 18 }}>Zui (Pet Champion)</div>
            <div style={{ color: "#71717A", fontSize: 13 }}>Marshmallow PBR • Anime Eyes • Twitchy Ears</div>
          </div>
        </div>

        <div style={{ width: 1, height: 40, backgroundColor: "rgba(255, 255, 255, 0.1)" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              backgroundColor: "rgba(192, 132, 252, 0.15)",
              border: "1px solid rgba(192, 132, 252, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
            }}
          >
            🔮
          </div>
          <div>
            <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 18 }}>Bolo (Ball Companion)</div>
            <div style={{ color: "#71717A", fontSize: 13 }}>Spring Antenna Physics • Shocked Mode</div>
          </div>
        </div>
      </div>
    </div>
  );
};
